import React, { useState, useEffect, useCallback } from "react";
import MetaTags from "react-meta-tags";
import { Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
  Button,
  Spinner,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input
} from "reactstrap";
import classnames from "classnames";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { get, post, put, del } from "../../helpers/api_helper";
import { NOTIFICATIONS_URL, TASKS_URL, PATIENT_URL } from "../../helpers/url_helper";
import cookieHelper from "../../helpers/getCookieData";

const TasksNotifications = (props) => {
  const { t } = useTranslation();

  const [activeMainTab, setActiveMainTab] = useState("TASKS"); // 'TASKS' | 'NOTIFICATIONS'
  const [taskFilter, setTaskFilter] = useState("all"); // 'all' | 'today' | 'overdue' | 'upcoming' | 'completed'
  const [notifFilter, setNotifFilter] = useState("ALL"); // 'ALL' | 'UNREAD' | 'BOOKING' | 'FOLLOW_UP' | 'PAYMENT' | 'LAB_REPORT'

  const [loadingTasks, setLoadingTasks] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [tasksPagination, setTasksPagination] = useState({ totalItems: 0, currentPage: 1 });

  const [loadingNotifs, setLoadingNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifsPagination, setNotifsPagination] = useState({ totalItems: 0, currentPage: 1 });

  // Modal for new task creation
  const [newTaskModal, setNewTaskModal] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: "",
    description: "",
    task_type: "GENERAL",
    priority: "NORMAL",
    due_at: moment().format("YYYY-MM-DDTHH:mm"),
    patient_id: ""
  });
  const [savingTask, setSavingTask] = useState(false);

  // Patients for dropdown
  const [patientsList, setPatientsList] = useState([]);

  // Snooze Modal
  const [snoozeModal, setSnoozeModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [snoozeHours, setSnoozeHours] = useState("2");

  // Get user role
  const getUserRole = () => {
    try {
      const userCookie = cookieHelper.getCookie("_u");
      if (userCookie) {
        const parsed = JSON.parse(atob(userCookie));
        return parsed?.role || parsed?.user_role?.name || "";
      }
    } catch (e) {
      // fallback
    }
    return "";
  };

  const userRole = getUserRole();
  const isAdmin = userRole.toLowerCase().includes("admin") || userRole.toLowerCase() === "super_admin";

  // Fetch Tasks
  const fetchTasks = useCallback(async (filter = taskFilter, page = 1) => {
    setLoadingTasks(true);
    try {
      let url = `${TASKS_URL}?page=${page}&limit=25`;
      if (filter === "completed") {
        url += `&status=COMPLETED`;
      } else if (filter === "overdue") {
        url += `&filter_due=overdue`;
      } else if (filter === "today") {
        url += `&filter_due=today`;
      } else if (filter === "upcoming") {
        url += `&filter_due=upcoming`;
      } else {
        url += `&status=PENDING`;
      }

      const res = await get(url);
      if (res && res.success) {
        setTasks(res.body?.items || []);
        setTasksPagination({
          totalItems: res.body?.totalItems || 0,
          currentPage: res.body?.currentPage || 1
        });
      }
    } catch (err) {
      console.error("Error fetching tasks:", err);
    } finally {
      setLoadingTasks(false);
    }
  }, [taskFilter]);

  // Fetch Notifications
  const fetchNotifications = useCallback(async (filter = notifFilter, page = 1) => {
    setLoadingNotifs(true);
    try {
      let url = `${NOTIFICATIONS_URL}?page=${page}&limit=25`;
      if (filter === "UNREAD") {
        url += `&is_read=false`;
      } else if (filter !== "ALL") {
        url += `&type=${filter}`;
      }

      const res = await get(url);
      if (res && res.success) {
        setNotifications(res.body?.items || []);
        setNotifsPagination({
          totalItems: res.body?.totalItems || 0,
          currentPage: res.body?.currentPage || 1
        });
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
    } finally {
      setLoadingNotifs(false);
    }
  }, [notifFilter]);

  // Load patients list for task modal
  const fetchPatients = async () => {
    try {
      const res = await get(`${PATIENT_URL}?limit=100`);
      if (res && (res.data || res.body)) {
        setPatientsList(res.data?.data || res.body?.rows || res.data || []);
      }
    } catch (err) {
      console.error("Error loading patients:", err);
    }
  };

  useEffect(() => {
    if (activeMainTab === "TASKS") {
      fetchTasks(taskFilter);
    } else {
      fetchNotifications(notifFilter);
    }
  }, [activeMainTab, taskFilter, notifFilter, fetchTasks, fetchNotifications]);

  // Task Actions
  const handleToggleTaskStatus = async (task) => {
    const newStatus = task.status === "COMPLETED" ? "PENDING" : "COMPLETED";
    try {
      await put(`${TASKS_URL}/${task.id}/status`, { status: newStatus });
      fetchTasks(taskFilter);
    } catch (err) {
      console.error("Error toggling task status:", err);
    }
  };

  const handleOpenSnooze = (task) => {
    setSelectedTask(task);
    setSnoozeModal(true);
  };

  const handleConfirmSnooze = async () => {
    if (!selectedTask) return;
    try {
      const snoozeTime = moment().add(parseInt(snoozeHours), "hours").toDate();
      await put(`${TASKS_URL}/${selectedTask.id}/status`, {
        status: "SNOOZED",
        snoozed_until: snoozeTime
      });
      setSnoozeModal(false);
      setSelectedTask(null);
      fetchTasks(taskFilter);
    } catch (err) {
      console.error("Error snoozing task:", err);
    }
  };

  const handleDeleteTask = async (id) => {
    if (!window.confirm(t("Are you sure you want to delete this task?"))) return;
    try {
      await del(`${TASKS_URL}/${id}`);
      fetchTasks(taskFilter);
    } catch (err) {
      console.error("Error deleting task:", err);
    }
  };

  const handleCreateTaskSubmit = async (e) => {
    e.preventDefault();
    if (!taskForm.title.trim()) return;

    setSavingTask(true);
    try {
      await post(TASKS_URL, {
        ...taskForm,
        patient_id: taskForm.patient_id || null
      });
      setNewTaskModal(false);
      setTaskForm({
        title: "",
        description: "",
        task_type: "GENERAL",
        priority: "NORMAL",
        due_at: moment().format("YYYY-MM-DDTHH:mm"),
        patient_id: ""
      });
      fetchTasks(taskFilter);
    } catch (err) {
      console.error("Error creating task:", err);
    } finally {
      setSavingTask(false);
    }
  };

  // Notification Actions
  const handleMarkNotifRead = async (id) => {
    try {
      await put(`${NOTIFICATIONS_URL}/${id}/read`);
      fetchNotifications(notifFilter);
    } catch (err) {
      console.error("Error marking read:", err);
    }
  };

  const handleMarkAllNotifsRead = async () => {
    try {
      await put(`${NOTIFICATIONS_URL}/mark-all-read`);
      fetchNotifications(notifFilter);
    } catch (err) {
      console.error("Error marking all read:", err);
    }
  };

  const handleDismissNotif = async (id) => {
    try {
      await put(`${NOTIFICATIONS_URL}/${id}/dismiss`);
      fetchNotifications(notifFilter);
    } catch (err) {
      console.error("Error dismissing notification:", err);
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case "URGENT":
        return <Badge color="danger">{t("Urgent")}</Badge>;
      case "HIGH":
        return <Badge color="warning">{t("High")}</Badge>;
      default:
        return <Badge color="info">{t("Normal")}</Badge>;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case "BOOKING":
        return <Badge color="primary">{t("Booking")}</Badge>;
      case "FOLLOW_UP":
        return <Badge color="warning">{t("Follow-up")}</Badge>;
      case "PAYMENT":
        return <Badge color="danger">💳 {t("Payment")}</Badge>;
      case "LAB_REPORT":
        return <Badge color="info">🧪 {t("Lab")}</Badge>;
      default:
        return <Badge color="secondary">{t("Notice")}</Badge>;
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{t("Tasks & Notifications")} | {process.env.REACT_APP_TITLE}</title>
        </MetaTags>
        <Container fluid>
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div>
              <h4 className="mb-1 text-dark fw-bold">{t("Tasks & Notifications")}</h4>
              <p className="text-muted mb-0 font-size-13">
                {t("Centralized clinical cockpit for patient follow-ups, appointments, and reminders.")}
              </p>
            </div>

            {activeMainTab === "TASKS" && (
              <Button
                color="primary"
                onClick={() => {
                  fetchPatients();
                  setNewTaskModal(true);
                }}
                className="d-flex align-items-center gap-1 shadow-sm font-size-13"
              >
                <i className="bx bx-plus font-size-16" />
                {t("Add New Task")}
              </Button>
            )}
          </div>

          {/* Main Navigation Tabs */}
          <Card className="shadow-sm border-0 mb-4">
            <CardBody className="p-2 border-bottom">
              <Nav tabs className="nav-tabs-custom border-bottom-0">
                <NavItem>
                  <NavLink
                    className={classnames("fw-semibold font-size-14 cursor-pointer", {
                      active: activeMainTab === "TASKS"
                    })}
                    onClick={() => setActiveMainTab("TASKS")}
                  >
                    <i className="bx bx-task me-1 text-primary" />
                    {t("Actionable Tasks & Reminders")}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames("fw-semibold font-size-14 cursor-pointer", {
                      active: activeMainTab === "NOTIFICATIONS"
                    })}
                    onClick={() => setActiveMainTab("NOTIFICATIONS")}
                  >
                    <i className="bx bx-bell me-1 text-warning" />
                    {t("Notifications & Alerts")}
                  </NavLink>
                </NavItem>
              </Nav>
            </CardBody>

            {/* TAB 1: Actionable Tasks */}
            <TabContent activeTab={activeMainTab}>
              <TabPane tabId="TASKS" className="p-3">
                {/* Sub Filters */}
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                  <Nav pills className="gap-1 font-size-12">
                    <NavItem>
                      <NavLink
                        className={classnames("cursor-pointer py-1 px-3", { active: taskFilter === "all" })}
                        onClick={() => setTaskFilter("all")}
                      >
                        {t("Pending Tasks")}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames("cursor-pointer py-1 px-3 text-danger", { active: taskFilter === "overdue" })}
                        onClick={() => setTaskFilter("overdue")}
                      >
                        🔴 {t("Overdue")}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames("cursor-pointer py-1 px-3 text-warning", { active: taskFilter === "today" })}
                        onClick={() => setTaskFilter("today")}
                      >
                        🟠 {t("Due Today")}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames("cursor-pointer py-1 px-3 text-primary", { active: taskFilter === "upcoming" })}
                        onClick={() => setTaskFilter("upcoming")}
                      >
                        🔵 {t("Upcoming")}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames("cursor-pointer py-1 px-3 text-success", { active: taskFilter === "completed" })}
                        onClick={() => setTaskFilter("completed")}
                      >
                        🟢 {t("Completed")}
                      </NavLink>
                    </NavItem>
                  </Nav>
                </div>

                {/* Tasks Table */}
                {loadingTasks ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <div className="text-muted mt-2 font-size-13">{t("Loading tasks...")}</div>
                  </div>
                ) : tasks.length === 0 ? (
                  <div className="text-center py-5 bg-light rounded text-muted">
                    <i className="bx bx-check-circle font-size-36 text-success mb-2 d-block" />
                    <h5 className="font-size-15 fw-bold">{t("No tasks found")}</h5>
                    <p className="font-size-13 text-muted">{t("No items match the selected filter.")}</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table hover className="align-middle table-nowrap mb-0">
                      <thead className="table-light font-size-12 text-muted">
                        <tr>
                          <th style={{ width: "40px" }}>{t("Status")}</th>
                          <th>{t("Task Title & Description")}</th>
                          <th>{t("Patient")}</th>
                          <th>{t("Type")}</th>
                          <th>{t("Priority")}</th>
                          <th>{t("Due Date & Time")}</th>
                          <th className="text-end">{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tasks.map((task) => {
                          const isDone = task.status === "COMPLETED";
                          const isOverdue = task.due_at && moment(task.due_at).isBefore(moment(), "day") && !isDone;

                          return (
                            <tr key={task.id} className={classnames({ "table-light opacity-75": isDone })}>
                              <td>
                                <Input
                                  type="checkbox"
                                  checked={isDone}
                                  onChange={() => handleToggleTaskStatus(task)}
                                  className="cursor-pointer"
                                />
                              </td>
                              <td>
                                <div className={classnames("fw-semibold font-size-13 text-dark", { "text-decoration-line-through text-muted": isDone })}>
                                  {task.title}
                                </div>
                                {task.description && (
                                  <div className="font-size-11 text-muted text-truncate" style={{ maxWidth: "320px" }}>
                                    {task.description}
                                  </div>
                                )}
                              </td>
                              <td>
                                {task.patient ? (
                                  <Link
                                    to={`/patient/details/${task.patient.id}`}
                                    className="text-primary fw-medium font-size-12"
                                  >
                                    👤 {task.patient.first_name} {task.patient.last_name || ""}
                                  </Link>
                                ) : (
                                  <span className="text-muted font-size-12">—</span>
                                )}
                              </td>
                              <td>
                                <span className="badge bg-soft-secondary text-secondary font-size-11">
                                  {task.task_type}
                                </span>
                              </td>
                              <td>{getPriorityBadge(task.priority)}</td>
                              <td>
                                <span className={classnames("font-size-12", { "text-danger fw-bold": isOverdue })}>
                                  <i className="mdi mdi-clock-outline me-1" />
                                  {task.due_at ? moment(task.due_at).format("DD MMM YYYY, hh:mm A") : "—"}
                                </span>
                              </td>
                              <td className="text-end">
                                <div className="d-flex align-items-center justify-content-end gap-1">
                                  {!isDone && (
                                    <Button
                                      color="light"
                                      size="sm"
                                      onClick={() => handleOpenSnooze(task)}
                                      title={t("Snooze")}
                                      className="btn-soft-warning font-size-11"
                                    >
                                      <i className="bx bx-time" />
                                    </Button>
                                  )}
                                  <Button
                                    color="light"
                                    size="sm"
                                    onClick={() => handleDeleteTask(task.id)}
                                    title={t("Delete")}
                                    className="btn-soft-danger font-size-11"
                                  >
                                    <i className="bx bx-trash" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </TabPane>

              {/* TAB 2: Notifications Archive */}
              <TabPane tabId="NOTIFICATIONS" className="p-3">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                  <Nav pills className="gap-1 font-size-12">
                    <NavItem>
                      <NavLink
                        className={classnames("cursor-pointer py-1 px-3", { active: notifFilter === "ALL" })}
                        onClick={() => setNotifFilter("ALL")}
                      >
                        {t("All")}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames("cursor-pointer py-1 px-3", { active: notifFilter === "UNREAD" })}
                        onClick={() => setNotifFilter("UNREAD")}
                      >
                        {t("Unread")}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames("cursor-pointer py-1 px-3", { active: notifFilter === "BOOKING" })}
                        onClick={() => setNotifFilter("BOOKING")}
                      >
                        {t("Bookings")}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames("cursor-pointer py-1 px-3", { active: notifFilter === "FOLLOW_UP" })}
                        onClick={() => setNotifFilter("FOLLOW_UP")}
                      >
                        {t("Follow-ups")}
                      </NavLink>
                    </NavItem>
                    {isAdmin && (
                      <NavItem>
                        <NavLink
                          className={classnames("cursor-pointer py-1 px-3 text-danger", { active: notifFilter === "PAYMENT" })}
                          onClick={() => setNotifFilter("PAYMENT")}
                        >
                          💳 {t("Payment Reminders")}
                        </NavLink>
                      </NavItem>
                    )}
                  </Nav>

                  <Button
                    color="light"
                    size="sm"
                    onClick={handleMarkAllNotifsRead}
                    className="font-size-12"
                  >
                    <i className="bx bx-check-double text-success me-1" />
                    {t("Mark All Read")}
                  </Button>
                </div>

                {/* Notifications Table */}
                {loadingNotifs ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <div className="text-muted mt-2 font-size-13">{t("Loading notifications...")}</div>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="text-center py-5 bg-light rounded text-muted">
                    <i className="bx bx-bell-off font-size-36 text-secondary opacity-50 mb-2 d-block" />
                    <h5 className="font-size-15 fw-bold">{t("No notifications found")}</h5>
                    <p className="font-size-13 text-muted">{t("You are completely caught up.")}</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table hover className="align-middle table-nowrap mb-0">
                      <thead className="table-light font-size-12 text-muted">
                        <tr>
                          <th>{t("Type")}</th>
                          <th>{t("Title & Message")}</th>
                          <th>{t("Priority")}</th>
                          <th>{t("Date & Time")}</th>
                          <th>{t("Status")}</th>
                          <th className="text-end">{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {notifications.map((notif) => {
                          const isUnread = !notif.read_at;

                          return (
                            <tr key={notif.id} className={classnames({ "bg-light bg-opacity-50 fw-semibold": isUnread })}>
                              <td>{getTypeBadge(notif.type)}</td>
                              <td>
                                <div className="font-size-13 text-dark mb-0">
                                  {notif.title}
                                </div>
                                {notif.message && (
                                  <div className="font-size-11 text-muted text-truncate" style={{ maxWidth: "400px" }}>
                                    {notif.message}
                                  </div>
                                )}
                              </td>
                              <td>{getPriorityBadge(notif.priority)}</td>
                              <td className="font-size-12 text-muted">
                                <i className="mdi mdi-clock-outline me-1" />
                                {moment(notif.created_at).format("DD MMM YYYY, hh:mm A")}
                              </td>
                              <td>
                                {isUnread ? (
                                  <Badge color="warning" pill>{t("Unread")}</Badge>
                                ) : (
                                  <Badge color="light" className="text-muted" pill>{t("Read")}</Badge>
                                )}
                              </td>
                              <td className="text-end">
                                <div className="d-flex align-items-center justify-content-end gap-1">
                                  {isUnread && (
                                    <Button
                                      color="light"
                                      size="sm"
                                      onClick={() => handleMarkNotifRead(notif.id)}
                                      title={t("Mark Read")}
                                      className="btn-soft-success font-size-11"
                                    >
                                      <i className="bx bx-check" />
                                    </Button>
                                  )}
                                  {notif.action_url && (
                                    <Link
                                      to={notif.action_url}
                                      className="btn btn-sm btn-soft-primary font-size-11"
                                      title={t("Open")}
                                    >
                                      <i className="bx bx-link-external" />
                                    </Link>
                                  )}
                                  <Button
                                    color="light"
                                    size="sm"
                                    onClick={() => handleDismissNotif(notif.id)}
                                    title={t("Dismiss")}
                                    className="btn-soft-secondary font-size-11"
                                  >
                                    <i className="bx bx-x" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </TabPane>
            </TabContent>
          </Card>

          {/* New Task Creation Modal */}
          <Modal isOpen={newTaskModal} toggle={() => setNewTaskModal(!newTaskModal)} centered>
            <ModalHeader toggle={() => setNewTaskModal(!newTaskModal)}>
              <i className="bx bx-plus-circle me-1 text-primary" />
              {t("Create New Task")}
            </ModalHeader>
            <form onSubmit={handleCreateTaskSubmit}>
              <ModalBody>
                <FormGroup className="mb-3">
                  <Label>{t("Task Title *")}</Label>
                  <Input
                    type="text"
                    required
                    placeholder={t("e.g. Call Rahul Sharma for 2nd sitting")}
                    value={taskForm.title}
                    onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  />
                </FormGroup>

                <Row>
                  <Col md="6">
                    <FormGroup className="mb-3">
                      <Label>{t("Task Type")}</Label>
                      <Input
                        type="select"
                        value={taskForm.task_type}
                        onChange={(e) => setTaskForm({ ...taskForm, task_type: e.target.value })}
                      >
                        <option value="GENERAL">{t("General Task")}</option>
                        <option value="CALL_PATIENT">{t("Call Patient")}</option>
                        <option value="REVIEW_REPORT">{t("Review Lab Report")}</option>
                        <option value="TREATMENT_PLAN">{t("Treatment Plan")}</option>
                        <option value="COLLECT_PAYMENT">{t("Payment Follow-up")}</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup className="mb-3">
                      <Label>{t("Priority")}</Label>
                      <Input
                        type="select"
                        value={taskForm.priority}
                        onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                      >
                        <option value="NORMAL">{t("Normal")}</option>
                        <option value="HIGH">{t("High")}</option>
                        <option value="URGENT">{t("Urgent")}</option>
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>

                <FormGroup className="mb-3">
                  <Label>{t("Due Date & Time *")}</Label>
                  <Input
                    type="datetime-local"
                    required
                    value={taskForm.due_at}
                    onChange={(e) => setTaskForm({ ...taskForm, due_at: e.target.value })}
                  />
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label>{t("Associated Patient (Optional)")}</Label>
                  <Input
                    type="select"
                    value={taskForm.patient_id}
                    onChange={(e) => setTaskForm({ ...taskForm, patient_id: e.target.value })}
                  >
                    <option value="">{t("Select Patient...")}</option>
                    {patientsList.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.first_name} {p.last_name || ""} ({p.case_no || p.mobile})
                      </option>
                    ))}
                  </Input>
                </FormGroup>

                <FormGroup className="mb-0">
                  <Label>{t("Description / Clinical Notes")}</Label>
                  <Input
                    type="textarea"
                    rows="3"
                    placeholder={t("Additional instructions or notes...")}
                    value={taskForm.description}
                    onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  />
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" type="button" onClick={() => setNewTaskModal(false)}>
                  {t("Cancel")}
                </Button>
                <Button color="primary" type="submit" disabled={savingTask}>
                  {savingTask ? t("Saving...") : t("Save Task")}
                </Button>
              </ModalFooter>
            </form>
          </Modal>

          {/* Snooze Modal */}
          <Modal isOpen={snoozeModal} toggle={() => setSnoozeModal(!snoozeModal)} centered size="sm">
            <ModalHeader toggle={() => setSnoozeModal(!snoozeModal)}>
              <i className="bx bx-time text-warning me-1" />
              {t("Snooze Task")}
            </ModalHeader>
            <ModalBody>
              <p className="font-size-13 text-muted mb-3">
                {t("Choose how long to snooze this task:")}
              </p>
              <FormGroup>
                <Input
                  type="select"
                  value={snoozeHours}
                  onChange={(e) => setSnoozeHours(e.target.value)}
                >
                  <option value="1">{t("1 Hour")}</option>
                  <option value="2">{t("2 Hours")}</option>
                  <option value="4">{t("4 Hours")}</option>
                  <option value="24">{t("Tomorrow Morning (24 Hours)")}</option>
                  <option value="48">{t("In 2 Days")}</option>
                  <option value="168">{t("Next Week (7 Days)")}</option>
                </Input>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" size="sm" onClick={() => setSnoozeModal(false)}>
                {t("Cancel")}
              </Button>
              <Button color="warning" size="sm" onClick={handleConfirmSnooze}>
                {t("Confirm Snooze")}
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TasksNotifications;
